import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Upload, Plus } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { AttachmentType } from '../../../interfaces';
import {
  REQUEST_CANDIDATE_ATTACHMENT_UPLOAD,
  CONFIRM_CANDIDATE_ATTACHMENT_UPLOAD,
  REQUEST_JOB_ATTACHMENT_UPLOAD,
  CONFIRM_JOB_ATTACHMENT_UPLOAD
} from '../../../GraphQL/attachments/mutations';

// File validation constants matching backend rules
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const validateFile = (file: File): string | null => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return 'Only PDF and Word documents (.pdf, .doc, .docx) are allowed';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'File size must be less than 10MB';
  }
  return null;
};

interface AttachmentUploadProps {
  entityId: number;
  type: 'candidate' | 'job';
  onUploadComplete?: () => void;
}

const getAttachmentTypeOptions = (type: 'candidate' | 'job') => {
  if (type === 'candidate') {
    return [
      { value: AttachmentType.Resume, label: 'Resume' },
      { value: AttachmentType.CoverLetter, label: 'Cover Letter' },
      { value: AttachmentType.Portfolio, label: 'Portfolio' },
      { value: AttachmentType.Certificate, label: 'Certificate' },
      { value: AttachmentType.Reference, label: 'Reference' },
      { value: AttachmentType.Other, label: 'Other' }
    ];
  } else {
    return [
      { value: AttachmentType.JobDescription, label: 'Job Description' },
      { value: AttachmentType.Contract, label: 'Contract' },
      { value: AttachmentType.CompanyDocument, label: 'Company Document' },
      { value: AttachmentType.JobSpecification, label: 'Job Specification' },
      { value: AttachmentType.PolicyDocument, label: 'Policy Document' },
      { value: AttachmentType.Other, label: 'Other' }
    ];
  }
};

const AttachmentUpload: React.FC<AttachmentUploadProps> = ({ entityId, type, onUploadComplete }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [attachmentType, setAttachmentType] = useState<AttachmentType>(
    type === 'candidate' ? AttachmentType.Resume : AttachmentType.JobDescription
  );
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStep, setUploadStep] = useState<'preparing' | 'uploading' | 'confirming'>('preparing');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [requestCandidateUpload] = useMutation(REQUEST_CANDIDATE_ATTACHMENT_UPLOAD);
  const [confirmCandidateUpload] = useMutation(CONFIRM_CANDIDATE_ATTACHMENT_UPLOAD);
  const [requestJobUpload] = useMutation(REQUEST_JOB_ATTACHMENT_UPLOAD);
  const [confirmJobUpload] = useMutation(CONFIRM_JOB_ATTACHMENT_UPLOAD);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setSelectedFile(file);
        setError(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Step 1: Request upload URL
      setUploadStep('preparing');
      const requestMutation = type === 'candidate' ? requestCandidateUpload : requestJobUpload;
      const requestInput = {
        ...(type === 'candidate' ? { candidateId: entityId } : { jobId: entityId }),
        fileName: selectedFile.name,
        contentType: selectedFile.type,
        fileSizeBytes: selectedFile.size,
        attachmentType,
        description: description.trim() || undefined
      };

      const { data: urlData } = await requestMutation({
        variables: { input: requestInput }
      });

      const uploadResponse = type === 'candidate'
        ? urlData?.requestCandidateAttachmentUpload
        : urlData?.requestJobAttachmentUpload;

      if (!uploadResponse?.uploadUrl) {
        throw new Error('Failed to get upload URL');
      }

      // Step 2: Upload directly to S3
      setUploadStep('uploading');
      await uploadToS3(uploadResponse.uploadUrl, selectedFile, uploadResponse.originalFileName, uploadResponse.uploadTimestamp);

      // Step 3: Confirm upload
      setUploadStep('confirming');
      const confirmMutation = type === 'candidate' ? confirmCandidateUpload : confirmJobUpload;
      const confirmInput = {
        ...(type === 'candidate' ? { candidateId: entityId } : { jobId: entityId }),
        storageKey: uploadResponse.storageKey,
        originalFileName: selectedFile.name,
        contentType: selectedFile.type,
        fileSizeBytes: selectedFile.size,
        attachmentType,
        description: description.trim() || undefined
      };

      await confirmMutation({
        variables: { input: confirmInput }
      });

      setShowModal(false);
      resetForm();
      onUploadComplete?.();
    } catch (error: any) {
      setError(error.message || 'Failed to upload attachment');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStep('preparing');
    }
  };

  const uploadToS3 = (uploadUrl: string, file: File, originalFileName: string, uploadTimestamp: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadProgress(100);
          resolve();
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('S3 upload failed'));
      });

      xhr.open('PUT', uploadUrl);

      // Set all headers that are included in the presigned URL signature
      // Use the exact values that were used when generating the presigned URL
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
      xhr.setRequestHeader('x-amz-meta-original-filename', originalFileName);
      xhr.setRequestHeader('x-amz-meta-upload-timestamp', uploadTimestamp);

      xhr.send(file);
    });
  };

  const resetForm = () => {
    setSelectedFile(null);
    setDescription('');
    setAttachmentType(type === 'candidate' ? AttachmentType.Resume : AttachmentType.JobDescription);
    setError(null);
    setUploadProgress(0);
    setUploadStep('preparing');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStepMessage = () => {
    switch (uploadStep) {
      case 'preparing':
        return 'Preparing upload...';
      case 'uploading':
        return 'Uploading to cloud storage...';
      case 'confirming':
        return 'Finalizing upload...';
      default:
        return 'Uploading...';
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setShowModal(false);
      resetForm();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const attachmentTypeOptions = getAttachmentTypeOptions(type);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowModal(true)}
        className="d-flex align-items-center gap-2"
      >
        <Plus size={16} />
        Upload Attachment
      </Button>

      <Dialog open={showModal} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={20} />
              Upload Attachment
            </DialogTitle>
          </DialogHeader>

          <div>
            {error && (
              <Alert variant="destructive" className="mb-3">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div>
                <Label htmlFor="attachmentType">Attachment Type</Label>
                <select
                  id="attachmentType"
                  className="w-full mt-1 rounded-md border border-gray-300 px-3 py-2"
                  value={attachmentType}
                  onChange={(e) => setAttachmentType(e.target.value as AttachmentType)}
                  disabled={uploading}
                >
                  {attachmentTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="file">File</Label>
                <Input
                  id="file"
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  accept=".pdf,.doc,.docx"
                  className="mt-1"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-500 mt-1">
                    Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <textarea
                  id="description"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description for this attachment..."
                  disabled={uploading}
                  className="w-full mt-1 rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              {uploading && (
                <div>
                  <Label className="text-sm text-gray-500">{getStepMessage()}</Label>
                  <Progress
                    value={uploadStep === 'uploading' ? uploadProgress : 100}
                    className="mt-2"
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AttachmentUpload;